import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatCurrency, formatDate, computePlan, variance } from "../lib/api";
import Gantt from "../components/Gantt";
import { assetUrl } from "../lib/brand";
import { CalendarDays, DollarSign, Clock, CreditCard, TrendingUp, AlertTriangle, Building2, ExternalLink, ShieldAlert } from "lucide-react";

export default function PublicProject() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/public/projects/${token}`).then(({ data }) => setData(data)).catch((e) => setErr(e?.response?.data?.detail || "Not found"));
  }, [token]);

  const plan = useMemo(() => data ? computePlan(data.tasks, data.roles, data.project?.time_unit || "Days") : null, [data]);
  const currency = data?.owner_currency || "USD";
  const brand = data?.brand || { org_name: "Compass PM", primary_color: "#1D4ED8", logo_url: null };

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 pc-grid-bg">
        <div className="pc-card p-8 max-w-md text-center">
          <div className="text-xl font-semibold text-white mb-2" style={{fontFamily:'Outfit'}}>Share link unavailable</div>
          <div className="text-slate-400 text-sm mb-4">{err}</div>
          <Link to="/" className="text-blue-400 hover:text-blue-300 text-sm">← Back to Compass PM</Link>
        </div>
      </div>
    );
  }
  if (!data || !plan) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading shared project…</div>;

  const { project, tasks } = data;
  const schedVar = variance(project.stated_due_date, plan.plannedDelivery);
  const estCostTotal = Number(project.estimated_cost_total || 0);
  const budgetVar = estCostTotal - Number(project.stated_budget || 0);
  const primary = brand.primary_color || "#1D4ED8";

  const cards = [
    { l: "Calculated Delivery", v: plan.plannedDelivery ? formatDate(plan.plannedDelivery) : "—", icon: CalendarDays, tone: "text-blue-300" },
    { l: "Estimated Cost", v: formatCurrency(estCostTotal, currency), icon: DollarSign, tone: "text-blue-300" },
    { l: "Actual Delivery", v: plan.actualDelivery ? formatDate(plan.actualDelivery) : "—", icon: Clock, tone: "text-slate-200" },
    { l: "Actual Cost", v: formatCurrency(plan.actualCost, currency), icon: CreditCard, tone: "text-slate-200" },
    { l: "Schedule Variance", v: schedVar === null ? "—" : schedVar > 0 ? `+${schedVar}d late` : `${Math.abs(schedVar)}d ahead`, icon: TrendingUp, tone: schedVar > 0 ? "text-rose-300" : "text-emerald-300" },
    { l: "Budget Variance", v: formatCurrency(budgetVar, currency), icon: AlertTriangle, tone: budgetVar > 0 ? "text-rose-300" : "text-emerald-300" },
  ];

  return (
    <div className="min-h-screen" style={{"--brand": primary}}>
      <header style={{background: `linear-gradient(90deg, ${primary} 0%, ${primary}d0 60%, transparent 100%)`}} className="border-b border-slate-800/60" data-testid="brand-header">
        <div className="max-w-[1300px] mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {brand.logo_url ? (
              <img src={assetUrl(brand.logo_url)} alt="org logo" className="h-10 w-auto bg-white/10 rounded px-2 py-1" onError={(e)=>{e.currentTarget.style.display='none';}} data-testid="brand-logo"/>
            ) : (
              <div className="w-10 h-10 rounded-lg bg-white/15 flex items-center justify-center">
                <Building2 className="text-white" size={20}/>
              </div>
            )}
            <div>
              <div className="font-bold text-white text-lg" style={{fontFamily:'Outfit'}} data-testid="brand-org-name">{brand.org_name}</div>
              {brand.division && <div className="text-xs uppercase tracking-widest text-white/80" data-testid="brand-division">{brand.division}</div>}
              {brand.subtitle && <div className="text-xs text-white/80">{brand.subtitle}</div>}
            </div>
          </div>
          <div className="text-xs text-white/70">Read-only share</div>
        </div>
      </header>

      <main className="max-w-[1300px] mx-auto px-6 py-10" data-testid="public-project">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-widest mb-1" style={{color: primary}}>{brand.org_name}</div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white" style={{fontFamily:'Outfit'}}>{project.name}</h1>
          {project.description && <p className="text-slate-400 mt-2 max-w-3xl">{project.description}</p>}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3 mb-6" data-testid="public-summary">
          {cards.map((c) => (
            <div key={c.l} className="pc-card p-4">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest" style={{color: primary}}><c.icon size={12}/> {c.l}</div>
              <div className={`text-xl font-bold mt-1 ${c.tone}`} style={{fontFamily:'Outfit'}}>{c.v}</div>
            </div>
          ))}
        </div>

        {data.share_show_risks && data.risks && data.risks.length > 0 && (
          <div className="pc-card p-5 mb-6 border border-rose-500/30 bg-rose-500/5" data-testid="public-top-risks">
            <div className="flex items-center gap-2 mb-3">
              <ShieldAlert className="text-rose-300" size={16} />
              <div className="text-xs uppercase tracking-widest text-rose-300">Top risks we're watching</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {data.risks.map((r, i) => {
                const sevTone = r.severity === "High" ? "text-rose-300 border-rose-500/40" : r.severity === "Low" ? "text-emerald-300 border-emerald-500/40" : "text-amber-300 border-amber-500/40";
                const key = `${r.severity}-${(r.description || "").slice(0, 32)}`;
                return (
                  <div key={key} className="pc-card p-3 border border-slate-800" data-testid={`public-risk-${i}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-[10px] uppercase tracking-widest text-slate-400">Risk {i + 1}</div>
                      <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${sevTone}`}>{r.severity}</span>
                    </div>
                    <div className="text-sm text-white font-medium leading-snug">{r.description}</div>
                    {r.mitigation && <div className="text-xs text-slate-400 mt-2">↳ {r.mitigation}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="pc-card overflow-x-auto mb-6">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-widest border-b border-slate-800" style={{color: primary}}>
              <tr><th className="p-3">#</th><th className="p-3">Task</th><th className="p-3">Role</th><th className="p-3">Resource</th><th className="p-3">Est</th><th className="p-3">Actual</th><th className="p-3">Status</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {tasks.map((t) => {
                const planT = plan.tasks.find(p => p.id === t.id);
                const isCritical = !!planT?._critical;
                return (
                  <tr key={t.id} className={isCritical ? "bg-rose-500/5" : ""}>
                    <td className="p-2 mono text-slate-400">{t.task_number}{isCritical && <span className="text-rose-400 ml-1">◆</span>}</td>
                    <td className="p-2 text-white">{t.name}</td>
                    <td className="p-2 text-slate-300">{t.role_name || "—"}</td>
                    <td className="p-2 text-slate-300">{t.resource_name || "—"}</td>
                    <td className="p-2 mono text-slate-300">{t.estimated_time ?? "—"}</td>
                    <td className="p-2 mono text-slate-300">{t.actual_time ?? "—"}</td>
                    <td className="p-2 text-slate-300">{t.status || "Not Started"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <Gantt tasks={plan.tasks} timeUnit={data?.project?.time_unit || "Days"}/>

        <div className="mt-8 text-center">
          <Link to="/" className="text-xs text-slate-500 hover:text-slate-300 inline-flex items-center gap-1">Powered by Compass PM <ExternalLink size={10}/></Link>
        </div>
      </main>
    </div>
  );
}
